/* eslint-disable array-callback-return */
import React, { useCallback, useEffect, useMemo, useState, useContext } from "react";
import {
  Container,
  Segment,
  Header,
  Icon,
  Grid,
  Label,
  Message,
  Button,
  Table,
  Input,
  Modal,
  Dimmer,
  Loader,
  Popup,
} from "semantic-ui-react";
import moment from "moment";
import { PDFViewer } from "@react-pdf/renderer";

import { db } from "../firebase/firebaseConfig";
import { getDocs, collection, where, query } from "firebase/firestore";
import { UserContext } from "../context/UserContext";

import PresupuestoPDF from "./ReportePresupuestoPDF";
import ReporteFinalPDF from "./ReporteFinalPresuPDF";

const COLORS = {
  teal: "#00b5ad",
  tealSoft: "#e8f8f7",
  green: "#21ba45",
  greenSoft: "#edf9f0",
  orange: "#f2711c",
  orangeSoft: "#fff5eb",
  red: "#db2828",
  border: "#e5e7eb",
  text: "#1f2937",
  muted: "#6b7280",
  background: "#f8fafc",
};

const cardStyle = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 8,
  boxShadow: "0 1px 3px rgba(0,0,0,.06)",
};

const stickyTh = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  background: "#f8fafc",
  color: "#374151",
  whiteSpace: "nowrap",
};

const safeText = (v) => {
  if (!v) return "";
  if (v?.toDate) return moment(v.toDate()).format("DD/MM/YYYY HH:mm");
  return String(v);
};

const ReportePresuPRO = () => {
  const { users } = useContext(UserContext);

  // ---------------- data ----------------
  const [ingresos, setIngresos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  // ---------------- ui ----------------
  const [loading, setLoading] = useState(true);
  const [buscador, setBuscador] = useState("");

  // ---------------- pdf ----------------
  const [openPresu, setOpenPresu] = useState(false);
  const [openCert, setOpenCert] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const [cabecera, setCabecera] = useState([]);
  const [presu, setPresu] = useState([]);
  const [falla, setFalla] = useState([]);
  const [total, setTotal] = useState(0);
  const [obs, setObs] = useState("");

  const [instru, setInstru] = useState([]);
  const [check, setCheck] = useState([]);
  const [medicion, setMedicion] = useState([]);
  const [seguridad, setSeguridad] = useState([]);
  const [userAdd, setUserAdd] = useState([]);

  // ---------------- helpers ----------------
  const formatearFecha = (f) =>
    f?.toDate ? moment(f.toDate()).format("DD/MM/YYYY HH:mm") : "";

  // ---------------- load base data ----------------
  const loadIngresos = useCallback(async () => {
    if (!users?.emp_id) return;

    const q = query(
      collection(db, "ingresostcab"),
      where("emp_id", "==", users.emp_id),
      where("estado", "==", "ENTREGADO")
    );

    const snap = await getDocs(q);
    setIngresos(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
  }, [users?.emp_id]);

  const loadUsuarios = useCallback(async () => {
    if (!users?.emp_id) return;

    const q = query(
      collection(db, "usuarios"),
      where("emp_id", "==", users.emp_id)
    );

    const snap = await getDocs(q);
    setUsuarios(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
  }, [users?.emp_id]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);

      try {
        await Promise.all([loadIngresos(), loadUsuarios()]);
      } catch (error) {
        console.error("Error cargando presupuestos:", error);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [loadIngresos, loadUsuarios]);

  // ---------------- solo presupuestos ----------------
  const dataPresu = useMemo(() => {
    return [...ingresos]
      .filter(
        (x) =>
          x.servicio === "PRESUPUESTO" ||
          x.servicionew === "PRESUPUESTO"
      )
      .sort((a, b) => (b.fecha_out ?? 0) - (a.fecha_out ?? 0));
  }, [ingresos]);

  // ---------------- search ----------------
  const filteredData = useMemo(() => {
    const q = buscador.trim().toLowerCase();

    if (!q) return dataPresu;

    return dataPresu.filter((item) =>
      Object.keys(item).some((k) =>
        safeText(item[k]).toLowerCase().includes(q)
      )
    );
  }, [dataPresu, buscador]);

  // ---------------- KPIs ----------------
  const kpis = useMemo(() => {
    const total = dataPresu.length;
    const certificados = dataPresu.filter(
      (x) => x.estado_servicio === "REALIZADO"
    ).length;

    return {
      total,
      certificados,
      pendientes: total - certificados,
    };
  }, [dataPresu]);

  // ---------------- PDF Presupuesto ----------------
  const verPresupuesto = async (id) => {
    setPdfLoading(true);
    setOpenPresu(true);

    try {
      const cab = dataPresu.filter((x) => x.id === id);
      setCabecera(cab);

      const qCab = query(
        collection(db, "presupuestoscab"),
        where("emp_id", "==", users.emp_id),
        where("id_cab_inst", "==", id)
      );

      const cabSnap = await getDocs(qCab);
      const cabId = cabSnap.docs?.[0]?.id;

      const qDet = query(
        collection(db, "presupuestos"),
        where("emp_id", "==", users.emp_id),
        where("id_cab_pre", "==", cabId)
      );

      const detSnap = await getDocs(qDet);
      const det = detSnap.docs.map((d) => ({
        ...d.data(),
        id: d.id,
      }));

      setPresu(det.filter((d) => d.categoria !== "FALLA"));
      setFalla(det.filter((d) => d.categoria === "FALLA"));
      setTotal(
        det.reduce((acc, d) => acc + Number(d.price || 0), 0)
      );

      const tecnico = cab?.[0]?.tecnico;
      setUserAdd(usuarios.filter((u) => u.correo === tecnico));
    } finally {
      setPdfLoading(false);
    }
  };

  // ---------------- PDF Certificado ----------------

  const verCertificado = async (id) => {
    setPdfLoading(true);
    setOpenCert(true);

    // Evitar reutilizar datos de otro presupuesto
    setCabecera([]);
    setPresu([]);
    setFalla([]);
    setTotal(0);
    setObs("");
    setInstru([]);
    setCheck([]);
    setMedicion([]);
    setSeguridad([]);
    setUserAdd([]);

    try {
      const cab = dataPresu.filter((x) => x.id === id);
      setCabecera(cab);

      // 1. Recuperar el presupuesto asociado a la orden
      const qCabPre = query(
        collection(db, "presupuestoscab"),
        where("emp_id", "==", users.emp_id),
        where("id_cab_inst", "==", id)
      );

      const cabPreSnap = await getDocs(qCabPre);
      const cabPreId = cabPreSnap.docs?.[0]?.id;

      if (!cabPreId) {
        throw new Error(
          "No se encontró la cabecera del presupuesto"
        );
      }

      const qDetalle = query(
        collection(db, "presupuestos"),
        where("emp_id", "==", users.emp_id),
        where("id_cab_pre", "==", cabPreId)
      );

      const detalleSnap = await getDocs(qDetalle);

      const detalle = detalleSnap.docs.map((d) => ({
        ...d.data(),
        id: d.id,
      }));

      setPresu(
        detalle.filter(
          (d) =>
            d.categoria === "SERVICIO" ||
            d.categoria === "REPUESTO"
        )
      );

      setFalla(
        detalle.filter((d) => d.categoria === "FALLA")
      );

      setTotal(
        detalle.reduce(
          (acc, d) => acc + Number(d.price || 0),
          0
        )
      );

      // 2. Recuperar la bitácora del certificado
      const qCabBi = query(
        collection(db, "bitacoracabpresu"),
        where("emp_id", "==", users.emp_id),
        where("id_ingreso", "==", id)
      );

      const cabBiSnap = await getDocs(qCabBi);
      const cabBi = cabBiSnap.docs?.[0];

      if (!cabBi) {
        throw new Error(
          "No se encontró la bitácora del certificado"
        );
      }

      setObs(cabBi.data()?.observaciones || "");

      const qBit = query(
        collection(db, "bitacorapresu"),
        where("emp_id", "==", users.emp_id),
        where("id_cab_pre", "==", cabBi.id)
      );

      const bitSnap = await getDocs(qBit);

      const bit = bitSnap.docs.map((d) => ({
        ...d.data(),
        id: d.id,
      }));

      setInstru(
        bit.filter((b) => b.categoria === "INSTRUMENTOS")
      );

      setCheck(
        bit.filter((b) => b.categoria === "CHECK")
      );

      setMedicion(
        bit.filter((b) => b.categoria === "MEDICION")
      );

      setSeguridad(
        bit.filter((b) => b.categoria === "SEGURIDAD")
      );

      const useradd = cabBi.data()?.useradd;

      setUserAdd(
        usuarios.filter((u) => u.correo === useradd)
      );

      console.log(
        "Repuestos del certificado:",
        detalle.filter((d) => d.categoria === "REPUESTO")
      );

    } catch (error) {
      console.error("Error al generar certificado:", error);

      setOpenCert(false);
      window.alert(
        "No se pudo cargar el certificado: " + error.message
      );
    } finally {
      setPdfLoading(false);
    }
  };



  // ---------------- render ----------------
  return (
    <Container
      fluid
      style={{
        background: COLORS.background,
        minHeight: "100vh",
        padding: "18px 16px 35px",
      }}
    >
      <Container fluid style={{ maxWidth: 1400 }}>

        {/* CABECERA */}
        <Segment
          style={{
            ...cardStyle,
            padding: "18px 20px",
            marginBottom: 16,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <Label
              size="massive"
              style={{
                background: COLORS.tealSoft,
                color: COLORS.teal,
                margin: 0,
              }}
            >
              <Icon name="file alternate outline" style={{ margin: 0 }} />
            </Label>

            <Header
              as="h2"
              style={{
                margin: 0,
                color: COLORS.text,
                fontSize: 21,
              }}
            >
              Presupuestos
              <Header.Subheader style={{ marginTop: 5 }}>
                Consulta de presupuestos y certificados de Servicio Técnico
              </Header.Subheader>
            </Header>
          </div>
        </Segment>

        {/* KPIs */}
        <Grid stackable columns={3} style={{ marginBottom: 8 }}>
          <Grid.Column>
            <Segment style={{ ...cardStyle, margin: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Label
                  size="large"
                  style={{
                    background: COLORS.tealSoft,
                    color: COLORS.teal,
                  }}
                >
                  <Icon name="file alternate outline" />
                </Label>

                <div>
                  <div
                    style={{
                      color: COLORS.muted,
                      fontSize: 12,
                    }}
                  >
                    Total presupuestos
                  </div>

                  <strong
                    style={{
                      fontSize: 23,
                      color: COLORS.text,
                    }}
                  >
                    {kpis.total}
                  </strong>
                </div>
              </div>
            </Segment>
          </Grid.Column>

          <Grid.Column>
            <Segment style={{ ...cardStyle, margin: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Label
                  size="large"
                  style={{
                    background: COLORS.greenSoft,
                    color: COLORS.green,
                  }}
                >
                  <Icon name="check circle outline" />
                </Label>

                <div>
                  <div
                    style={{
                      color: COLORS.muted,
                      fontSize: 12,
                    }}
                  >
                    Certificados
                  </div>

                  <strong
                    style={{
                      fontSize: 23,
                      color: COLORS.text,
                    }}
                  >
                    {kpis.certificados}
                  </strong>
                </div>
              </div>
            </Segment>
          </Grid.Column>

          <Grid.Column>
            <Segment style={{ ...cardStyle, margin: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Label
                  size="large"
                  style={{
                    background: COLORS.orangeSoft,
                    color: COLORS.orange,
                  }}
                >
                  <Icon name="clock outline" />
                </Label>

                <div>
                  <div
                    style={{
                      color: COLORS.muted,
                      fontSize: 12,
                    }}
                  >
                    Pendientes
                  </div>

                  <strong
                    style={{
                      fontSize: 23,
                      color: COLORS.text,
                    }}
                  >
                    {kpis.pendientes}
                  </strong>
                </div>
              </div>
            </Segment>
          </Grid.Column>
        </Grid>

        {/* BUSCADOR */}
        <Segment style={{ ...cardStyle, marginTop: 12 }}>
          <Grid stackable verticalAlign="middle">
            <Grid.Row>
              <Grid.Column width={13}>
                <Header as="h4" style={{ marginBottom: 10 }}>
                  <Icon name="filter" color="teal" />

                  <Header.Content>
                    Búsqueda de presupuestos
                    <Header.Subheader>
                      Orden, RUT, cliente, equipo, serie, estado u otro dato
                    </Header.Subheader>
                  </Header.Content>
                </Header>

                <Input
                  fluid
                  focus
                  placeholder="Escriba para buscar..."
                  value={buscador}
                  onChange={(e) => setBuscador(e.target.value)}
                  icon={{
                    name: buscador.trim() ? "close" : "search",
                    link: true,
                    onClick: () =>
                      buscador.trim()
                        ? setBuscador("")
                        : null,
                  }}
                />
              </Grid.Column>

              <Grid.Column width={3}>
                <Button
                  basic
                  fluid
                  icon
                  labelPosition="left"
                  onClick={() => setBuscador("")}
                  disabled={!buscador}
                >
                  <Icon name="eraser" />
                  Limpiar
                </Button>
              </Grid.Column>
            </Grid.Row>

            {!!buscador.trim() && (
              <Grid.Row>
                <Grid.Column width={16}>
                  <Message
                    info
                    size="small"
                    style={{ margin: 0 }}
                  >
                    <Icon name="info circle" />
                    Mostrando <b>{filteredData.length}</b>{" "}
                    resultado(s) para <b>{buscador}</b>.
                  </Message>
                </Grid.Column>
              </Grid.Row>
            )}
          </Grid>
        </Segment>

        {/* RESULTADOS */}
        <Segment
          raised
          style={{
            ...cardStyle,
            padding: 0,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "13px 16px",
              borderBottom: `1px solid ${COLORS.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Header as="h4" style={{ margin: 0 }}>
              <Icon
                name="list alternate outline"
                color="teal"
              />

              <Header.Content>
                Presupuestos realizados
                <Header.Subheader>
                  Presupuestos y certificados asociados
                </Header.Subheader>
              </Header.Content>
            </Header>

            <Label basic color="teal">
              {filteredData.length} registro(s)
            </Label>
          </div>

          {loading ? (
            <div
              style={{
                height: 220,
                position: "relative",
              }}
            >
              <Dimmer active inverted>
                <Loader size="large">
                  Procesando...
                </Loader>
              </Dimmer>
            </div>
          ) : filteredData.length === 0 ? (
            <Message
              warning
              icon
              style={{ margin: 16 }}
            >
              <Icon name="warning circle" />

              <Message.Content>
                <Message.Header>
                  Sin registros
                </Message.Header>

                {buscador.trim()
                  ? "No se encontraron coincidencias para la búsqueda."
                  : "No existen presupuestos registrados."}
              </Message.Content>
            </Message>
          ) : (
            <div
              style={{
                maxHeight: 520,
                overflow: "auto",
              }}
            >
              <Table
                celled
                compact="very"
                striped
                selectable
                singleLine
                style={{
                  fontSize: 11,
                  margin: 0,
                }}
              >
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell style={stickyTh}>
                      #
                    </Table.HeaderCell>

                    <Table.HeaderCell style={stickyTh}>
                      Orden
                    </Table.HeaderCell>

                    <Table.HeaderCell style={stickyTh}>
                      Cliente
                    </Table.HeaderCell>

                    <Table.HeaderCell style={stickyTh}>
                      Equipo
                    </Table.HeaderCell>

                    <Table.HeaderCell style={stickyTh}>
                      Serie
                    </Table.HeaderCell>

                    <Table.HeaderCell style={stickyTh}>
                      Fecha
                    </Table.HeaderCell>

                    <Table.HeaderCell style={stickyTh}>
                      Estado
                    </Table.HeaderCell>

                    <Table.HeaderCell
                      style={stickyTh}
                      textAlign="center"
                    >
                      Presupuesto
                    </Table.HeaderCell>

                    <Table.HeaderCell
                      style={stickyTh}
                      textAlign="center"
                    >
                      Certificado
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {filteredData.map((r, i) => (
                    <Table.Row key={r.id}>
                      <Table.Cell>
                        {i + 1}
                      </Table.Cell>

                      <Table.Cell
                        style={{ fontWeight: 600 }}
                      >
                        {r.folio}
                      </Table.Cell>

                      <Table.Cell
                        style={{
                          whiteSpace: "normal",
                          fontWeight: 600,
                        }}
                      >
                        {r.entidad || "-"}
                      </Table.Cell>

                      <Table.Cell
                        style={{
                          whiteSpace: "normal",
                        }}
                      >
                        {r.tipo || "-"}
                      </Table.Cell>

                      <Table.Cell>
                        {r.serie || "-"}
                      </Table.Cell>

                      <Table.Cell
                        style={{
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatearFecha(r.fecha_out) || "-"}
                      </Table.Cell>

                      <Table.Cell>
                        {r.estado_servicio ===
                          "REALIZADO" ? (
                          <Label
                            basic
                            color="green"
                            size="tiny"
                          >
                            REALIZADO
                          </Label>
                        ) : (
                          <Label
                            basic
                            color="grey"
                            size="tiny"
                          >
                            {r.estado_servicio || "-"}
                          </Label>
                        )}
                      </Table.Cell>

                      {/* PRESUPUESTO */}
                      <Table.Cell textAlign="center">
                        <Popup
                          content="Ver presupuesto"
                          position="top center"
                          trigger={
                            <Button
                              basic
                              compact
                              icon
                              size="mini"
                              onClick={() =>
                                verPresupuesto(r.id)
                              }
                              style={{
                                color: COLORS.red,
                                background: "#fff",
                                boxShadow: "none",
                              }}
                            >
                              <Icon
                                name="file pdf outline"
                                color="red"
                                style={{
                                  margin: 0,
                                  fontSize: "1.25em",
                                }}
                              />
                            </Button>
                          }
                        />
                      </Table.Cell>

                      {/* CERTIFICADO */}
                      <Table.Cell textAlign="center">
                        {r.estado_servicio ===
                          "REALIZADO" ? (
                          <Popup
                            content="Ver certificado"
                            position="top center"
                            trigger={
                              <Button
                                basic
                                compact
                                icon
                                size="mini"
                                onClick={() =>
                                  verCertificado(r.id)
                                }
                                style={{
                                  color: COLORS.red,
                                  background: "#fff",
                                  boxShadow: "none",
                                }}
                              >
                                <Icon
                                  name="file pdf outline"
                                  color="red"
                                  style={{
                                    margin: 0,
                                    fontSize: "1.25em",
                                  }}
                                />
                              </Button>
                            }
                          />
                        ) : (
                          <Button
                            basic
                            compact
                            icon
                            size="mini"
                            disabled
                          >
                            <Icon
                              name="file pdf outline"
                              style={{ margin: 0 }}
                            />
                          </Button>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
        </Segment>

        {/* MODAL PDF */}
        {[openPresu, openCert].some(Boolean) && (
          <Modal
            open={openPresu || openCert}
            onClose={() => {
              setOpenPresu(false);
              setOpenCert(false);
            }}
            closeOnDimmerClick={false}
            closeOnEscape
            closeIcon
            size="large"
          >
            <Modal.Header>
              <Icon
                name="file pdf outline"
                color="red"
              />

              {openPresu
                ? "Presupuesto"
                : "Certificado"}

              {cabecera?.[0]?.folio && (
                <Label
                  basic
                  style={{ marginLeft: 10 }}
                >
                  Folio {cabecera[0].folio}
                </Label>
              )}
            </Modal.Header>

            <Modal.Content style={{ padding: 0 }}>
              {pdfLoading ? (
                <div style={{ height: 560 }}>
                  <Dimmer active inverted>
                    <Loader size="large">
                      Procesando...
                    </Loader>
                  </Dimmer>
                </div>
              ) : (
                <PDFViewer
                  width="100%"
                  height="560"
                  style={{
                    border: "1px solid #ddd",
                  }}
                >
                  {openPresu ? (
                    <PresupuestoPDF
                      cabecera={cabecera}
                      presu={presu}
                      falla={falla}
                      total={total}
                      usuario={userAdd}
                      empresa={users.emp_id}
                    />
                  ) : (
                    <ReporteFinalPDF
                      cabecera={cabecera}
                      presu={presu}
                      falla={falla}
                      total={total}
                      obs={obs}
                      instrumento={instru}
                      check={check}
                      medicion={medicion}
                      seguridad={seguridad}
                      usuario={userAdd}
                      empresa={users.emp_id}
                    />
                  )}
                </PDFViewer>
              )}
            </Modal.Content>

            <Modal.Actions>
              <Button
                color="teal"
                icon
                labelPosition="left"
                onClick={() => {
                  setOpenPresu(false);
                  setOpenCert(false);
                }}
              >
                <Icon name="close" />
                Cerrar
              </Button>
            </Modal.Actions>
          </Modal>
        )}
      </Container>
    </Container>
  );
};

export default ReportePresuPRO;